import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

// Create the context
const MessageContext = createContext();

// Custom hook to use the message context
export const useMessage = () => {
  return useContext(MessageContext);
};

// HTTP status code translations
const httpStatusCodes = {
  200: 'OK',
  201: 'Created',
  204: 'No Content',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  409: 'Conflict',
  422: 'Unprocessable Content',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout'
};

// Message provider component
export const MessageProvider = ({ children }) => {
  const [messageLog, setMessageLog] = useState([]);
  const messageLogRef = useRef(null);

  // Scroll to the bottom of the message log
  const scrollToMessageLogBottom = () => {
    if (messageLogRef.current) {
      messageLogRef.current.scrollTo({
        top: messageLogRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Format message with HTTP status code translation if present
  const formatMessage = (message) => {
    // Check for HTTP status codes in the message
    return message.replace(/\b([1-5][0-9][0-9])\b/g, (match) => {
      const code = parseInt(match);
      return httpStatusCodes[code] ? `${match} (${httpStatusCodes[code]})` : match;
    });
  };

  // Add a message to the message log
  const addMessage = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const formattedMessage = formatMessage(message);
    const newMessage = {
      id: Date.now(),
      text: formattedMessage,
      type: type, // 'info', 'success', 'error', or 'warning'
      timestamp: timestamp
    };
    
    setMessageLog(prevLog => [...prevLog, newMessage]);
    
    // Schedule scroll to bottom
    setTimeout(scrollToMessageLogBottom, 100);
  };

  // Clear all messages
  const clearMessages = () => {
    setMessageLog([]);
  };

  // Success message shorthand
  const success = (message) => {
    addMessage(message, 'success');
  };

  // Error message shorthand
  const error = (message) => {
    addMessage(message, 'error');
  };

  // Warning message shorthand
  const warning = (message) => {
    addMessage(message, 'warning');
  };

  // Info message shorthand
  const info = (message) => {
    addMessage(message, 'info');
  };

  // Auto-scroll when new messages are added
  useEffect(() => {
    scrollToMessageLogBottom();
  }, [messageLog]);

  // Provide the context value
  const value = {
    messageLog,
    addMessage,
    clearMessages,
    messageLogRef,
    success,
    error,
    warning,
    info
  };

  return (
    <MessageContext.Provider value={value}>
      {children}
      <div className="message-log-container">
        <div className="message-log-header">
          <h3>System Messages</h3>
          <button 
            className="clear-log-button" 
            onClick={clearMessages}
            title="Clear message log"
          >
            Clear
          </button>
        </div>
        <div className="message-log" ref={messageLogRef}>
          {messageLog.length === 0 ? (
            <div className="no-messages">No messages to display</div>
          ) : (
            messageLog.map(message => (
              <div key={message.id} className={`log-message ${message.type}`}>
                <span className="message-time">{message.timestamp}</span>
                <span className="message-text">{message.text}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </MessageContext.Provider>
  );
};